'use client';
import React, { useRef, useEffect, useState } from 'react';

export default function ContactSection() {
  const [visible, setVisible] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', service: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref?.current) observer?.observe(ref?.current);
    return () => observer?.disconnect();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const msg = `Hello Shruti, I am ${formData.name}. I am interested in ${formData.service || 'healing services'}. ${formData.message}. My email: ${formData.email}. Phone: ${formData.phone}`;
    window.open(`https://wa.me/917049753125?text=${encodeURIComponent(msg)}`, '_blank');
    setSubmitted(true);
  };

  return (
    <section
      id="contact"
      className="relative py-24 overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #0d0818 0%, #130a24 50%, #0d0818 100%)' }}
    >
      <div className="absolute inset-0 lotus-bg opacity-30 pointer-events-none" />
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/4 glow-orb-lavender" style={{ width: '400px', height: '400px', opacity: 0.35 }} />
        <div className="absolute bottom-0 right-1/4 glow-orb-gold" style={{ width: '350px', height: '350px', opacity: 0.3 }} />
      </div>

      <div className="max-w-6xl mx-auto px-6 relative z-10">
        <div
          ref={ref}
          className={`transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {/* Header */}
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="h-px w-12 bg-gradient-to-r from-transparent to-[#c9a84c]/60" />
              <span className="text-[#c9a84c] text-xs tracking-[0.4em] uppercase font-medium">Get In Touch</span>
              <div className="h-px w-12 bg-gradient-to-l from-transparent to-[#c9a84c]/60" />
            </div>
            <h2
              className="font-serif text-4xl md:text-5xl font-bold mb-4"
              style={{
                background: 'linear-gradient(135deg, #c4b5fd 0%, #9b7fe8 50%, #c9a84c 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Contact Us
            </h2>
            <div className="section-divider" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left: Contact Info */}
            <div className="space-y-5">
              {/* WhatsApp */}
              <a
                href="https://wa.me/917049753125"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 p-5 rounded-2xl transition-all duration-300 hover:scale-[1.02] group"
                style={{ background: 'rgba(37,211,102,0.08)', border: '1px solid rgba(37,211,102,0.25)' }}
              >
                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(37,211,102,0.15)' }}>
                  <svg viewBox="0 0 24 24" fill="#25D366" className="w-6 h-6">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                </div>
                <div>
                  <div className="text-[#25D366] font-semibold text-sm">WhatsApp</div>
                  <div className="text-[#e8dff5]/80 text-sm">+91 70497 53125</div>
                  <div className="text-[#c4b5fd]/40 text-xs mt-0.5">Chat with us anytime</div>
                </div>
              </a>

              {/* Call Now */}
              <a
                href="tel:+917049753125"
                className="flex items-center gap-4 p-5 rounded-2xl transition-all duration-300 hover:scale-[1.02]"
                style={{ background: 'rgba(22, 12, 40, 0.80)', border: '1px solid rgba(155,127,232,0.22)' }}
              >
                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(155,127,232,0.12)' }}>
                  <span className="text-[#9b7fe8] text-xl">📞</span>
                </div>
                <div>
                  <div className="text-[#9b7fe8] font-semibold text-sm">Call Now</div>
                  <div className="text-[#e8dff5]/80 text-sm">+91 70497 53125</div>
                  <div className="text-[#c4b5fd]/40 text-xs mt-0.5">Mon–Sat, 9 AM – 7 PM</div>
                </div>
              </a>

              {/* Email */}
              <a
                href="mailto:shreevidhahealing@gmail.com"
                className="flex items-center gap-4 p-5 rounded-2xl transition-all duration-300 hover:scale-[1.02]"
                style={{ background: 'rgba(22, 12, 40, 0.80)', border: '1px solid rgba(201,168,76,0.22)' }}
              >
                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(201,168,76,0.12)' }}>
                  <span className="text-[#c9a84c] text-xl">✉</span>
                </div>
                <div>
                  <div className="text-[#c9a84c] font-semibold text-sm">Email</div>
                  <div className="text-[#e8dff5]/80 text-sm">shreevidhahealing@gmail.com</div>
                </div>
              </a>

              {/* Address */}
              <div
                className="flex items-start gap-4 p-5 rounded-2xl"
                style={{ background: 'rgba(22, 12, 40, 0.80)', border: '1px solid rgba(201,168,76,0.18)' }}
              >
                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(201,168,76,0.10)' }}>
                  <span className="text-[#c9a84c] text-xl">📍</span>
                </div>
                <div>
                  <div className="text-[#c9a84c] font-semibold text-sm mb-1">Address</div>
                  <div className="text-[#e8dff5]/70 text-sm leading-relaxed">
                    Shreevidha Healing Hub<br />
                    India (Online & In-Person Sessions Available)
                  </div>
                  <a
                    href="https://maps.google.com/?q=Shreevidha+Healing+Hub"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#c9a84c] text-xs mt-2 inline-block hover:text-[#e8c96a] transition-colors"
                  >
                    View on Google Maps →
                  </a>
                </div>
              </div>

              {/* Social Links */}
              <div
                className="p-5 rounded-2xl"
                style={{ background: 'rgba(22, 12, 40, 0.80)', border: '1px solid rgba(201,168,76,0.18)' }}
              >
                <div className="text-[#c9a84c] text-xs tracking-widest uppercase mb-4 font-medium">Follow Us</div>
                <div className="flex gap-3 flex-wrap">
                  {[
                    { label: 'Instagram', href: 'https://www.instagram.com/shreevidhahealing?igsh=czdsZzljcHB0dmp3', icon: '📸' },
                    { label: 'Facebook', href: 'https://www.facebook.com/profile.php?id=61566030202223', icon: '👥' },
                    { label: 'YouTube', href: 'https://www.youtube.com/@ShreeVidhaHealingHubb', icon: '▶️' },
                    { label: 'WhatsApp', href: 'https://chat.whatsapp.com/JBAssqjgZSQ89CRz3EMpqn', icon: '💬' },
                  ].map((social) => (
                    <a
                      key={social.label}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex flex-col items-center gap-1 px-3 py-2 rounded-xl text-xs text-[#e8dff5]/70 hover:text-[#c9a84c] transition-all duration-200 hover:scale-105"
                      style={{ background: 'rgba(155,127,232,0.08)', border: '1px solid rgba(155,127,232,0.15)' }}
                    >
                      <span className="text-lg">{social.icon}</span>
                      <span>{social.label}</span>
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: Contact Form */}
            <div
              className="rounded-3xl p-8"
              style={{
                background: 'rgba(22, 12, 40, 0.85)',
                border: '1px solid rgba(201,168,76,0.25)',
                boxShadow: '0 8px 40px rgba(0,0,0,0.3)',
              }}
            >
              {submitted ? (
                <div className="text-center py-12">
                  <div className="text-5xl mb-4">✨</div>
                  <h3 className="font-serif text-2xl text-[#c9a84c] mb-3">Thank You!</h3>
                  <p className="text-[#c4b5fd]/70 text-sm leading-relaxed">
                    Your message has been sent via WhatsApp. Shruti will get back to you shortly.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="mt-6 btn-outline-gold text-xs"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <>
                  <h3 className="font-serif text-xl font-semibold text-[#e8dff5] mb-6">Send a Message</h3>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[#c4b5fd]/60 text-xs tracking-widest uppercase mb-2">Your Name *</label>
                        <input
                          type="text"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="Priya Sharma"
                          className="w-full px-4 py-3 rounded-xl text-sm text-[#e8dff5] placeholder-[#c4b5fd]/30 outline-none transition-all duration-200 focus:border-[#c9a84c]/60"
                          style={{ background: 'rgba(13,8,24,0.7)', border: '1px solid rgba(155,127,232,0.25)' }}
                        />
                      </div>
                      <div>
                        <label className="block text-[#c4b5fd]/60 text-xs tracking-widest uppercase mb-2">Phone</label>
                        <input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          placeholder="+91 98765 43210"
                          className="w-full px-4 py-3 rounded-xl text-sm text-[#e8dff5] placeholder-[#c4b5fd]/30 outline-none transition-all duration-200 focus:border-[#c9a84c]/60"
                          style={{ background: 'rgba(13,8,24,0.7)', border: '1px solid rgba(155,127,232,0.25)' }}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-[#c4b5fd]/60 text-xs tracking-widest uppercase mb-2">Email *</label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="priya@example.com"
                        className="w-full px-4 py-3 rounded-xl text-sm text-[#e8dff5] placeholder-[#c4b5fd]/30 outline-none transition-all duration-200 focus:border-[#c9a84c]/60"
                        style={{ background: 'rgba(13,8,24,0.7)', border: '1px solid rgba(155,127,232,0.25)' }}
                      />
                    </div>
                    <div>
                      <label className="block text-[#c4b5fd]/60 text-xs tracking-widest uppercase mb-2">Service of Interest</label>
                      <select
                        value={formData.service}
                        onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl text-sm text-[#e8dff5] outline-none transition-all duration-200"
                        style={{ background: 'rgba(13,8,24,0.7)', border: '1px solid rgba(155,127,232,0.25)' }}
                      >
                        <option value="">Select a service...</option>
                        <option value="Quantum Karmic Healing">Quantum Karmic Healing</option>
                        <option value="Clinical Hypnotherapy">Clinical Hypnotherapy</option>
                        <option value="Past Life Regression">Past Life Regression</option>
                        <option value="Family Constellation">Family Constellation</option>
                        <option value="Akashic Records Reading">Akashic Records Reading & Healing</option>
                        <option value="Reiki Healing">Reiki Healing</option>
                        <option value="Tarot Guidance">Tarot Guidance</option>
                        <option value="Emotional Wellness Sessions">Emotional Wellness Sessions</option>
                        <option value="Courses & Workshops">Courses & Workshops</option>
                        <option value="General Inquiry">General Inquiry</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[#c4b5fd]/60 text-xs tracking-widest uppercase mb-2">Message *</label>
                      <textarea
                        required
                        rows={4}
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        placeholder="Tell us about yourself and what you're seeking healing for..."
                        className="w-full px-4 py-3 rounded-xl text-sm text-[#e8dff5] placeholder-[#c4b5fd]/30 outline-none transition-all duration-200 resize-none focus:border-[#c9a84c]/60"
                        style={{ background: 'rgba(13,8,24,0.7)', border: '1px solid rgba(155,127,232,0.25)' }}
                      />
                    </div>
                    <button type="submit" className="btn-gold w-full text-center">
                      Send Message via WhatsApp
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
